import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { SwapHorizontalCircle } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { DemandPosition } from '../../../services/demandService';
import { useCategoryTerms } from '../../../utils/categoryTerms';
import { useFx } from '../../../hooks/useFx';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';
import { ExposureHorizonWindow } from '../../../utils/exposureHorizon';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { filterByFormula, isOutOfScope as checkPositionOutOfScope } from '../../../utils/positionUtils';
import { PositionsGrid, GroupByConfig } from '../../../components/PositionsGrid';
import { getCurrencySymbol, formatCurrency, formatQuantity } from '../../../components/PositionsGrid/gridUtils';

interface PriceInfo {
  price: number;
  currency: string;
  originalPrice?: number;
  originalCurrency?: string;
}

interface DemandGridProps {
  positions: DemandPosition[];
  formulaId: string;
  formulaName: string;
  colorsMap: Record<string, string>;
  pricesMap: Record<string, PriceInfo>;
  priceAsOfDate: string | null;
  exposureWindow?: ExposureHorizonWindow;
}

export const DemandGrid: React.FC<DemandGridProps> = ({ positions, formulaId, formulaName, colorsMap, pricesMap, priceAsOfDate, exposureWindow }) => {
  const { demand: demandTerm } = useCategoryTerms();
  const { materialsMap } = useMaterialsStore();
  const isGeneralMode = formulaId === 'general';
  const baseCurrency = useBaseCurrency();
  const { convertToBase } = useFx(baseCurrency);

  const formattedDate = priceAsOfDate
    ? new Date(priceAsOfDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : 'N/A';

  const filteredPositions = useMemo(() => {
    if (formulaId === 'general') return positions;
    return filterByFormula(positions, formulaId);
  }, [positions, formulaId]);

  // ── Row mapper ────────────────────────────────────────────
  const positionToRow = React.useCallback(
    (p: DemandPosition, idx: number) => {
      const priceInfo = pricesMap[p.formula_code];
      const spotTotal = priceInfo ? p.quantity * priceInfo.price : 0;
      let contractTotal: number | null = 0;
      if (p.unit_price_amount && p.unit_price_amount > 0) {
        const { amountBase } = convertToBase(p.quantity * p.unit_price_amount, p.unit_price_currency || baseCurrency);
        contractTotal = amountBase ?? null;
      }

      return {
        id: `${p.id}-${idx}`,
        formula_code: p.formula_code,
        formula_name: materialsMap[p.formula_code]?.name || p.formula_name || p.formula_code,
        customer_name: p.customer_name || 'Unknown',
        delivery_period: p.delivery_period,
        quantity: p.quantity,
        original_quantity: p.original_quantity,
        proration_factor: p.proration_factor,
        spot_price: priceInfo?.price || null,
        spot_currency: priceInfo?.currency || baseCurrency,
        contract_price: p.unit_price_amount,
        contract_currency: p.unit_price_currency,
        contract_total: contractTotal,
        spot_total: spotTotal,
        order_reference: p.order_reference,
        isOutOfScope: exposureWindow ? checkPositionOutOfScope(p, exposureWindow) : false,
      };
    },
    [exposureWindow, pricesMap, convertToBase, baseCurrency, materialsMap],
  );

  // ── Summary row builder ─────────────────────────────────
  const buildSummaryRow = React.useCallback(
    (allRows: Record<string, any>[]) => {
      const totalQty = allRows.reduce((s, r) => s + Number(r.quantity || 0), 0);
      const totalContract = allRows.reduce((s, r) => s + (r.contract_total || 0), 0);
      const totalSpot = allRows.reduce((s, r) => s + (r.spot_total || 0), 0);

      // Weighted avg contract price (always valid for any row set)
      const pricedRows = allRows.filter(r => r.contract_price > 0);
      const pricedQty = pricedRows.reduce((s, r) => s + Number(r.quantity || 0), 0);
      const pricedValue = pricedRows.reduce((s, r) => s + (r.contract_total || 0), 0);

      // Spot price: only show when all rows share the same spot (same formula)
      const spotPrices = new Set(allRows.map(r => r.spot_price).filter(Boolean));
      const sameSpot = spotPrices.size === 1;

      return {
        id: '__summary__',
        formula_code: '',
        formula_name: 'Total',
        customer_name: '',
        delivery_period: '',
        quantity: totalQty,
        spot_price: sameSpot ? (allRows[0]?.spot_price || null) : null,
        spot_currency: sameSpot ? (allRows[0]?.spot_currency || '') : '',
        contract_price: totalQty > 0 && pricedQty > 0 ? pricedValue / pricedQty : null,
        contract_currency: totalQty > 0 && pricedQty > 0 ? baseCurrency : '',
        contract_total: totalContract,
        spot_total: totalSpot,
        order_reference: '',
        isSummary: true,
      };
    },
    [baseCurrency],
  );

  // ── Banner summary helper ─────────────────────────────────
  const buildSummary = React.useCallback(
    (children: Record<string, any>[]) => {
      const totalQty = children.reduce((s, r) => s + Number(r.quantity), 0);
      const totalSpot = children.reduce((s, r) => s + (r.spot_total || 0), 0);
      return `${formatQuantity(totalQty)} MT · ${getCurrencySymbol(baseCurrency)}${formatCurrency(Math.round(totalSpot))} ${baseCurrency}`;
    },
    [baseCurrency],
  );

  // ── Group configurations ──────────────────────────────────
  const groupConfigs: GroupByConfig[] = useMemo(() => [
    {
      key: 'Formula',
      modes: ['general'],
      isDefaultFor: 'general',
      getGroupKey: (row) => row.formula_code,
      getGroupLabel: (key) => materialsMap[key]?.name || key,
      getGroupSummary: (_key: string, children: Record<string, any>[]) => buildSummary(children),
      getGroupColor: (key: string) => colorsMap[key] || '#94a3b8',
    },
    {
      key: 'Month',
      modes: ['general', 'formula'],
      isDefaultFor: 'formula',
      getGroupKey: (row) => row.delivery_period?.substring(0, 7) || 'Unknown',
      getGroupLabel: (key) => {
        const [y, m] = key.split('-');
        return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      },
      getGroupSummary: (_key: string, children: Record<string, any>[]) => buildSummary(children),
    },
    {
      key: 'Customer',
      modes: ['formula'],
      getGroupKey: (row) => row.customer_name || 'Unknown',
      getGroupLabel: (key) => key,
      getGroupSummary: (_key: string, children: Record<string, any>[]) => buildSummary(children),
    },
    {
      key: 'None',
      modes: ['formula'],
    },
  ], [buildSummary, colorsMap, materialsMap]);

  const formulaColor = colorsMap[formulaId] || '#6b7280';

  // ── Column definitions ────────────────────────────────────
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'formula_name',
      headerName: 'Formula',
      flex: 1,
      minWidth: 140,
      renderCell: (params: any) => <div className="text-slate-700">{params.value}</div>,
    },
    {
      field: 'customer_name',
      headerName: 'Customer',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: any) => <div className="text-slate-600">{params.value}</div>,
    },
    {
      field: 'delivery_period',
      headerName: 'Delivery',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params: any) => {
        const date = new Date(params.value);
        return <div className="text-slate-600">{date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>;
      },
    },
    {
      field: 'quantity',
      headerName: 'Quantity (MT)',
      flex: 0.7,
      minWidth: 110,
      renderCell: (params: any) => {
        const row = params.row;
        const isProrated = row.original_quantity != null && row.proration_factor != null;
        if (isProrated) {
          const pct = Math.round(row.proration_factor * 100);
          return (
            <Tooltip title={`Pro-rated from ${formatQuantity(Number(row.original_quantity))} MT (${pct}% of month remaining)`} arrow>
              <div className="text-slate-700 flex items-center gap-1">
                <span>{formatQuantity(Number(params.value))}</span>
                <span className="text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 leading-tight">adj</span>
              </div>
            </Tooltip>
          );
        }
        return <div className="text-slate-700">{formatQuantity(Number(params.value))}</div>;
      },
    },
    {
      field: 'spot_price',
      headerName: 'Spot Price',
      flex: 0.7,
      minWidth: 110,
      renderHeader: () => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold">Spot Price</span>
          <span className="text-[10px] text-slate-400 font-normal">as of {formattedDate}</span>
        </div>
      ),
      renderCell: (params: any) => {
        const price = params.value;
        const isAgg = params.row.isSummary || params.row._isGroupRow;
        if (!price) return (isAgg && isGeneralMode) ? <div /> : <div className="text-slate-300 text-[11px] italic">Not provided</div>;
        const cur = params.row.spot_currency || baseCurrency;
        return (
          <div className="text-slate-600">
            {getCurrencySymbol(cur)}{Number(price).toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-xs text-slate-400">{cur}</span>
          </div>
        );
      },
    },
    {
      field: 'contract_price',
      headerName: 'Contract Price',
      flex: 0.7,
      minWidth: 110,
      renderCell: (params: any) => {
        const row = params.row;
        const isAgg = row.isSummary || row._isGroupRow;
        if (!row.contract_price) return <div className="text-slate-300 text-[11px] italic">Not provided</div>;
        const isConverted = !isAgg && row.contract_currency && row.contract_currency !== 'MULTI' &&
          row.contract_currency.toUpperCase() !== baseCurrency.toUpperCase();
        return (
          <Tooltip title={isAgg ? 'Weighted average contract price' : ''} arrow enterDelay={300}>
            <div className="text-slate-700 flex items-center gap-1">
              <span>{getCurrencySymbol(row.contract_currency)}{Number(row.contract_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              {row.contract_currency && row.contract_currency !== baseCurrency && (
                <span className="text-xs text-slate-400">{row.contract_currency}</span>
              )}
              {isAgg && <span className="text-[10px] font-medium text-slate-300 bg-slate-50 border border-slate-200 rounded px-1 leading-tight">avg</span>}
              {isConverted && (
                <Tooltip title={`Original currency: ${row.contract_currency}`} arrow>
                  <SwapHorizontalCircle sx={{ fontSize: 14, color: '#94a3b8' }} />
                </Tooltip>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'contract_total',
      headerName: `Contract Value (${baseCurrency})`,
      flex: 0.8,
      minWidth: 130,
      renderCell: (params: any) => {
        if (params.value === null) return <div className="text-slate-300 text-[11px] italic">Converting…</div>;
        const v = Number(params.value);
        return v > 0 ? (
          <div className="text-slate-700">{getCurrencySymbol(baseCurrency)}{formatCurrency(Math.round(v))} <span className="text-xs text-slate-400">{baseCurrency}</span></div>
        ) : <div className="text-slate-300 text-[11px] italic">Not provided</div>;
      },
    },
    {
      field: 'spot_total',
      headerName: `Spot Value (${baseCurrency})`,
      flex: 0.8,
      minWidth: 130,
      renderHeader: () => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold">Spot Value ({baseCurrency})</span>
          <span className="text-[10px] text-slate-400 font-normal">as of {formattedDate}</span>
        </div>
      ),
      renderCell: (params: any) => {
        const v = Number(params.value);
        return v > 0 ? (
          <div className="text-slate-700">{getCurrencySymbol(baseCurrency)}{formatCurrency(Math.round(v))} <span className="text-xs text-slate-400">{baseCurrency}</span></div>
        ) : <div className="text-slate-300 text-[11px] italic">Not provided</div>;
      },
    },
    {
      field: 'order_reference',
      headerName: 'Reference',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params: any) => <div className="text-slate-500">{params.value || '—'}</div>,
    },
  ], [baseCurrency, formulaColor, formattedDate, isGeneralMode]);

  const formulaCount = isGeneralMode ? new Set(positions.map(p => p.formula_code)).size : undefined;

  // ── Render ────────────────────────────────────────────────
  return (
    <PositionsGrid
      positions={filteredPositions}
      positionToRow={positionToRow}
      columns={columns}
      groupConfigs={groupConfigs}
      buildSummaryRow={buildSummaryRow}
      categoryTerm={demandTerm}
      isGeneralMode={isGeneralMode}
      formulaId={formulaId}
      formulaName={formulaName}
      formulaColor={formulaColor}
      uniqueFormulas={formulaCount}
    />
  );
};
